import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-guidance",
  title: "A prayer for guidance — when you have to decide",
  h1: "A prayer for guidance",
  description:
    "A prayer for a decision you cannot see the end of, four passages on how guidance actually arrives, and what it almost never looks like.",
  targets: [
    "prayer for guidance",
    "prayer for a decision",
    "prayer for direction in life",
    "prayer to know god's will",
  ],
  published: "2026-09-03",
  standfirst:
    "Most guidance does not arrive as an answer. It arrives as the next step, which is usually smaller and duller than the one you asked for.",
  takeaways: [
    "Ask for the next step, not the map. Scripture's promises about guidance are almost all about the immediate road rather than the destination.",
    "Proverbs 3:6 says he will make your paths straight — the making is his verb, and it happens while you walk, not before you set out.",
    "If the decision is between two good options, you may be waiting for a certainty that was never on offer. At some point wisdom is choosing.",
  ],
  when: [
    "The night before you have to give an answer.",
    "When two options both look right and you have run out of new information.",
    "When you have been asking for months and nothing has arrived.",
    "When you already know the answer and are hoping for a different one.",
  ],
  prayer: [
    "God, I have to decide, and I cannot see far enough down either road to know which one is right.",
    "I am not asking for the whole map. I am asking for the next step, and for the nerve to take it before I am certain, because I am not going to be certain.",
    "If I have already been told and I have been pretending not to hear, say it again plainly, and let me stop negotiating.",
    "Keep me from choosing out of fear and calling it wisdom. Keep me from choosing out of impatience and calling it faith.",
    "And if I get this wrong, meet me on the wrong road. I need that more than I need to be right.",
  ],
  short: "God, not the map. The next step, and the nerve to take it.",
  honest: [
    "This prayer does not produce certainty, and Scripture does not promise it. Almost every biblical account of guidance is retrospective — people worked out afterwards where they had been led, and very few of them knew at the time.",
    "It also will not do the research. Wisdom in Proverbs is overwhelmingly practical: ask people who know, count the cost, notice your own pattern. Praying instead of finding out is not trust, it is avoidance with better vocabulary.",
    "And it is not a way of outsourcing responsibility. You will still have chosen, and living with having chosen is part of what is being asked of you.",
  ],
  verses: [
    {
      verse: {
        text: "I will instruct you and teach you in the way which you shall go. I will counsel you with my eye on you.",
        ref: "Psalm 32:8",
      },
      note: "The next verse is what gives this its edge: do not be like the horse or the mule, which have to be controlled with bit and bridle. The guidance on offer is the kind that works between two parties paying attention to each other, and the alternative described is not being left alone — it is being steered by force.",
    },
    {
      verse: {
        text: "But if any of you lacks wisdom, let him ask of God, who gives to all liberally and without reproach, and it will be given to him.",
        ref: "James 1:5",
      },
      note: "Without reproach is doing a lot of work. The fear being answered is that asking will be met with a remark about how you should have known already, which is exactly what keeps people from asking. Note also what is promised: wisdom, the capacity to judge, rather than the answer itself.",
    },
    {
      verse: {
        text: "Show me your ways, Yahweh. Teach me your paths. Guide me in your truth, and teach me, for you are the God of my salvation. I wait for you all day long.",
        ref: "Psalm 25:4–5",
      },
      note: "Four requests and three of them are for teaching rather than for information. That is the difference between wanting a decision made for you and wanting to become the sort of person who can make it. The last line admits how long that takes, and in the psalm it is not a figure of speech.",
    },
    {
      verse: {
        text: "Trust in Yahweh with all your heart, and don't lean on your own understanding. In all your ways acknowledge him, and he will make your paths straight.",
        ref: "Proverbs 3:5–6",
      },
      note: "Make straight is a road-building image rather than a map-reading one, and the tense matters: the path is straightened as it is travelled. Anyone waiting to see the straight path before setting out is waiting for something the proverb does not offer, which is why it frustrates people who read it as a promise of clarity in advance.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "The next step is the unit",
      id: "the-next-step-is-the-unit",
    },
    {
      type: "p",
      text: "Abraham is told to go to a land that will be shown to him. The pillar of cloud moves and the camp moves after it. Philip is sent to a road and finds out why once he is on it. The pattern is remarkably consistent and remarkably unsatisfying: enough light for the next stretch, and not more.",
    },
    {
      type: "p",
      text: "So a prayer that asks to see the whole thing is asking for a kind of guidance almost absent from the book it is being asked from. Ask for the step you can take this week.",
    },
    {
      type: "h2",
      text: "Two good options",
      id: "two-good-options",
    },
    {
      type: "p",
      text: "A lot of paralysis comes from assuming exactly one option is correct and the other is a mistake with consequences. Often both are permissible, one is slightly braver, and the deciding factor is which of them you are avoiding.",
    },
    {
      type: "p",
      text: "Wisdom literature is comfortable with this in a way modern Christian decision-making frequently is not. {Proverbs} does not tell you which job to take. It tells you what kind of person to be while taking it — and if you want a lighter way into the same question, {the character quiz} asks it sideways.",
      links: {
        Proverbs: "/verse/proverbs-3-5-6",
        "the character quiz": "/quiz/which-bible-character-are-you",
      },
    },
    {
      type: "h2",
      text: "When you already know",
      id: "when-you-already-know",
    },
    {
      type: "p",
      text: "There is a version of this prayer that is not a request. It is a negotiation, said repeatedly, in the hope that the answer changes — and it can run for months while looking from the outside like diligent seeking.",
    },
    {
      type: "note",
      text: "A test worth being honest about: if a friend described your situation to you in exactly these words, what would you tell them to do? If the answer arrives instantly, you are not short of guidance.",
    },
  ],
  faq: [
    {
      q: "How do I know if God is guiding a decision?",
      a: "Usually in retrospect, and usually through ordinary means — counsel from people who know you, circumstances opening or closing, and the settled sense that stays once the excitement goes. Scripture almost never describes guidance as certainty in advance.",
    },
    {
      q: "Which Bible verse is best for guidance?",
      a: 'Proverbs 3:5–6, as long as "make your paths straight" is read as road-building done while you walk rather than a map handed over before you leave. James 1:5 is the one for the asking itself.',
    },
    {
      q: "What if I pray for guidance and get nothing?",
      a: "Then use wisdom, which is what James 1:5 actually offers. Take counsel, weigh the cost, choose the braver of two good options, and go. Silence is frequently permission.",
    },
  ],
  relatedPrayers: ["prayer-for-patience", "prayer-for-strength"],
  related: ["phone-rules-for-christian-families", "morning-quiet-time-routine"],
  relatedTopics: [
    "bible-verses-about-time",
    "bible-verses-about-gods-promises",
  ],
  relatedPassages: ["proverbs-3-5-6", "jeremiah-29-11"],
};
